'use client';

import { usePathname } from 'next/navigation';
import { useState, type FC, type ReactNode } from 'react';
import { Flex } from '@eqi-investimentos/react';
import { captureException } from '@sentry/nextjs';
import {
  ContentModeEnum,
  type Content,
  type ContentMode,
} from '@/modules/Content/interface/Content';
import { useT } from '@/libs/i18n/utils/useT';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import {
  type ContentUpdateProgress,
  useUpdateWatchTime,
} from '@/modules/Content/http/client/mutations/useUpdateWatchTime';
import { EntertainmentSection } from '@/modules/Content/ui/ContentScreen/EntertainmentSection';
import { ContentFullDetails } from '@/modules/Content/ui/ContentScreen/FullDetails';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useBookmark } from '@/modules/Entertainment/http/client/mutations/useBookmark';
import { ResearchTabs } from '@/modules/Research/ui/Tabs';
import { getCookie } from '@/utils/cookies';
import { IfRender } from '@/utils/jsx';
import { useGetCTAContent } from '../../http/client/useGetCTAContent';
import { ContentAudioMode } from './Audio';
import { EducationalSection } from './EducationalSection';
import { ContentScreenLive } from './Live';
import { ContentSlideMode } from './Slide';
import { ContentTextMode } from './Text';
import { ContentVideoMode } from './Video';

export const ContentScreen: FC<ContentScreenProps> = ({
  authors,
  content,
  initialContentMode,
  initialProgress,
  isEqi,
  programType,
  children,
}) => {
  const t = useT();

  const { mutate: bookmarkContent, isPending } = useBookmark();
  const { mutate: createSFOpportunity } = useCreateSFOpportunity();
  const { mutate: updateWatchTime } = useUpdateWatchTime();
  const { mutate: sendClickEvent } = useSendClickEvent();

  const { data: dataCTAContent } = useGetCTAContent(content.slug);

  const [isBookmarked, setIsBookmarked] = useState(content.bookmarked);
  const [contentMode, setContentMode] = useState(initialContentMode);
  const [progress, setProgress] = useState(initialProgress);

  const isAdvisoryPage = usePathname().includes('minha-assessoria');

  const handleBookmarkContent = () =>
    bookmarkContent(content, {
      onSuccess: () => setIsBookmarked((prev) => !prev),
    });

  const sendOpportunity = () => {
    createSFOpportunity({
      action: salesforceOpportunityConfig.banking.content.action,
      product: salesforceOpportunityConfig.banking.content.product,
    });

    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.openAccount.label(
        window.location.pathname
      ),
    });
  };

  const sendDownloadReportClick = () => {
    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.recommnendation.label(
        t('screens.content.downloadReport'),
        window.location.pathname
      ),
    });
  };

  const sendProgress = async (progress: number, duration: number) => {
    const deviceCode = getCookie('deviceCode');

    const data: ContentUpdateProgress = {
      type: 'content',
      content_id: content.id,
      watch_progress: progress,
      watch_total: duration,
      origin: 'web',
      time: 10,
      device_code: deviceCode,
    };

    setProgress(progress);

    updateWatchTime(data, {
      onError: (error) =>
        captureException({
          tag: 'content.updateWatchTime',
          error,
          message: `Erro ao enviar watch de content de id ${content.id}`,
        }),
    });
  };

  return (
    <>
      <ContentVideoMode
        {...{
          authors,
          content,
          contentMode,
          progress,
          sendProgress,
          setContentMode,
        }}
      />
      <ContentAudioMode
        {...{
          authors,
          content,
          contentMode,
          progress,
          sendProgress,
          setContentMode,
        }}
      />
      <ContentSlideMode content={content} contentMode={contentMode} />
      <ContentScreenLive content={content} contentMode={contentMode} />
      <Flex
        position="relative"
        flexDir="column"
        alignSelf="center"
        alignItems="center"
        px="2xl"
        my="6xl"
        gap="6xl"
        w="full"
        maxW="85.25rem"
        color="content.primary"
      >
        <ContentFullDetails
          bookmarkPending={isPending}
          isEqiCustomer={isEqi}
          handleBookmark={handleBookmarkContent}
          {...{
            authors,
            content,
            contentMode,
            dataCTAContent,
            isBookmarked,
            isAdvisoryPage,
            programType,
          }}
        />
        <ContentTextMode
          bookmarkContent={handleBookmarkContent}
          {...{
            content,
            contentMode,
            dataCTAContent,
            isBookmarked,
            isEqi,
            isPending,
            isAdvisoryPage,
            sendDownloadReportClick,
            sendOpportunity,
            setContentMode,
            t,
          }}
        />
        <EducationalSection {...{ content, programType, t }} />
        <EntertainmentSection
          seasons={content.seasons}
          programType={programType}
        />
        <IfRender
          condition={programType === 'recommendation' && !isAdvisoryPage}
        >
          <ResearchTabs
            programId={content.program_id}
            programTitle={content.program_title}
            disableRelated={contentMode !== ContentModeEnum.TEXT}
          />
        </IfRender>
        {children}
      </Flex>
    </>
  );
};

type ContentScreenProps = {
  authors: string;
  content: Content;
  initialContentMode: ContentMode;
  initialProgress: number;
  isEqi: boolean;
  programType: string;
  children?: ReactNode;
};
